import React, { useState } from 'react';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import Button from 'src/components/Button/Button';
import Input from 'src/components/Input/Input';
import RadioButtonGroup from 'src/components/RadioButtonGroup/RadioButtonGroup';
import LoadState from 'src/components/LoadState/LoadState';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import Accordion from 'src/components/Accordion/Accordion';
import NewBadge from 'src/components/NewBadge/NewBadge';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import { TicketStatus } from 'src/pages/ServiceRequest/CustomHooks/useTicketStatuses';
import {
  useStatusManagerOrchestrator,
  UseStatusManagerOrchestratorReturn,
} from './useStatusManagerOrchestrator';
import { STATUS_LABEL_SUGGESTIONS } from './statusLabelSuggestions';
import { STATUS_DESCRIPTIONS } from './statusDescriptions';
import './status-manager.scss';

const LABEL_MAX_LENGTH: number = 40;
const ACTIVE_PILL_COLOR: string = 'var(--color-teal)';
const SUGGESTION_PILL_COLOR: string = 'var(--color-dark)';

interface StatusManagerRowProps {
  status: TicketStatus;
  suggestions: string[];
  disabled: boolean;
  onSetLabel: (status: TicketStatus, label: string) => Promise<boolean>;
  onClearLabel: (status: TicketStatus) => Promise<boolean>;
}

const StatusManagerRow = ({
  status,
  suggestions,
  disabled,
  onSetLabel,
  onClearLabel,
}: StatusManagerRowProps) => {
  const hasCustom: boolean = status.customLabel != null;

  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>('');
  const [confirmingReset, setConfirmingReset] = useState<boolean>(false);
  const [confirmingSet, setConfirmingSet] = useState<boolean>(false);

  const handleModeChange = (next: string): void => {
    if (next === 'default') {
      if (hasCustom) {
        setConfirmingReset(true);
        return;
      }

      setEditing(false);
      return;
    }

    setDraft('');
    setEditing(true);
  };

  const confirmReset = async (): Promise<void> => {
    await onClearLabel(status);
    setConfirmingReset(false);
  };

  const startReplace = (): void => {
    setDraft(status.customLabel ?? '');
    setEditing(true);
  };

  const confirmSet = async (): Promise<void> => {
    const ok: boolean = await onSetLabel(status, draft);
    setConfirmingSet(false);

    if (ok) {
      setEditing(false);
      setDraft('');
    }
  };

  const canSet: boolean = draft.trim().length > 0;

  const visibleSuggestions: string[] = suggestions.filter(
    (suggestion: string) => suggestion !== draft
  );

  const description: string =
    STATUS_DESCRIPTIONS[status.name] ?? status.description;

  const title = (
    <span className="status-manager-row-title">
      <span className="status-manager-row-name-line">
        <span className="status-manager-row-name">{status.name}</span>
        {hasCustom && status.customLabel && (
          <CategoryPill
            text={status.customLabel}
            size="tiny"
            color={ACTIVE_PILL_COLOR}
          />
        )}
      </span>
      {description && (
        <span className="status-manager-row-subtitle">{description}</span>
      )}
    </span>
  );

  return (
    <Accordion title={title} defaultOpen={hasCustom}>
      <div className="status-manager-body">
        <RadioButtonGroup
          name={`status-${status.id}`}
          direction="vertical"
          value={hasCustom || editing ? 'custom' : 'default'}
          onChange={handleModeChange}
          disabled={disabled}
          options={[
            { value: 'default', label: `Default — ${status.name}` },
            { value: 'custom', label: 'Custom label' },
          ]}
        />

        {hasCustom && !editing && (
          <div className="status-manager-committed">
            <CategoryPill
              text={status.customLabel}
              size="normal"
              color={ACTIVE_PILL_COLOR}
            />
            <Button
              outline="teal"
              size="tiny"
              label="Replace"
              disabled={disabled}
              onClick={startReplace}
            />
          </div>
        )}

        {editing && (
          <div className="status-manager-edit">
            <div className="status-manager-edit-row">
              <Input
                label=""
                block
                value={draft}
                placeholder={`Label for ${status.name}`}
                maxLength={LABEL_MAX_LENGTH}
                disabled={disabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft(event.target.value)
                }
              />
              <div className="status-manager-edit-actions">
                <Button
                  outline="teal"
                  size="tiny"
                  label="Set"
                  disabled={disabled || !canSet}
                  onClick={() => setConfirmingSet(true)}
                />
                <Button
                  outline="red"
                  size="tiny"
                  label="Cancel"
                  disabled={disabled}
                  onClick={() => setEditing(false)}
                />
              </div>
            </div>

            {visibleSuggestions.length > 0 && (
              <div className="status-manager-suggestions">
                <span className="status-manager-suggestions-label">
                  Suggestions
                </span>
                {visibleSuggestions.map((suggestion: string) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="status-manager-suggestion"
                    disabled={disabled}
                    onClick={() => setDraft(suggestion)}
                  >
                    <CategoryPill
                      text={suggestion}
                      size="small"
                      color={SUGGESTION_PILL_COLOR}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {confirmingReset && (
          <ConfirmationModal
            variant="warning"
            title={
              <>
                Reset <strong>{status.name}</strong> to its default label?
              </>
            }
            message={
              <>
                This removes the custom label and shows{' '}
                <strong>{status.name}</strong> again on every ticket with this
                status. This is visible to everyone. Are you sure you want to continue?
              </>
            }
            confirmLabel="Reset to default"
            cancelLabel="Keep custom label"
            requireTypeToConfirm
            confirmationWord="RESET"
            isSubmitting={disabled}
            onConfirm={confirmReset}
            onCancel={() => setConfirmingReset(false)}
          />
        )}

        {confirmingSet && (
          <ConfirmationModal
            variant="warning"
            title={
              <>
                Set <strong>{status.name}</strong> to "{draft.trim()}"?
              </>
            }
            message={
              <>
                Every <strong>{status.name}</strong> ticket will display{' '}
                <strong>{draft.trim()}</strong> instead of {status.name}. This is
                visible to everyone. Are you sure you want to continue?
              </>
            }
            confirmLabel="Set label"
            cancelLabel="Cancel"
            isSubmitting={disabled}
            onConfirm={confirmSet}
            onCancel={() => setConfirmingSet(false)}
          />
        )}
      </div>
    </Accordion>
  );
};

interface StatusManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onApplied?: () => void;
}

const StatusManager = ({ isOpen, onClose, onApplied }: StatusManagerProps) => {
  const {
    statuses,
    loading,
    submitting,
    onSetLabel,
    onClearLabel,
  }: UseStatusManagerOrchestratorReturn =
    useStatusManagerOrchestrator(onApplied);

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={onClose}
      title={
        <>
          Service request statuses
          <NewBadge className="status-manager-title-badge" />
        </>
      }
      description="Give any status a custom label shown in place of its name. Labels are display-only and never change how a status behaves."
      maxWidth="70rem"
      minHeight="40rem"
      footer={
        <Button color="teal" size="small" label="Done" onClick={onClose} />
      }
    >
      <div className="status-manager">
        <LoadState isLoading={loading} isEmpty={statuses.length === 0}>
          <div className="status-manager-list">
            {statuses.map((status: TicketStatus) => (
              <StatusManagerRow
                key={status.id}
                status={status}
                suggestions={STATUS_LABEL_SUGGESTIONS[status.name] ?? []}
                disabled={submitting}
                onSetLabel={onSetLabel}
                onClearLabel={onClearLabel}
              />
            ))}
          </div>
        </LoadState>
      </div>
    </GenericDialogue>
  );
};

export default StatusManager;
